import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    ACCOUNTING_AREA_ID_MARKER,
    processRequestForAccountingAreaWithReplacement,
} from './network/managedRequestForAccountingArea';
import { API } from '../constants';
import { PbDispatch } from './index';
import { PbRequest } from './pbRequest';
import { pbRequestReset } from './pbRequestSlice';

enum CurrencyType {
    EUR = 'EUR',
    GBP = 'GBP',
    SGD = 'SGD',
    CHF = 'CHF',
}

export enum BookingLogicPolicy {
    APPLIANCE_START = 'APPLIANCE_START',
    USER_DIRECT_BOOKING = 'USER_DIRECT_BOOKING',
    CALENDAR = 'CALENDAR',
}

enum ConnectivityState {
    UP = 'UP',
    DOWN = 'DOWN',
    CRITICAL = 'CRITICAL',
    INITIAL = 'INITIAL',
    WARNING = 'WARNING',
}

enum ConnectivityCheckState {
    NONE = 'NONE',
    REQUIRED = 'REQUIRED',
    IN_PROGRESS = 'IN_PROGRESS',
}

enum LocationLifecycleStatus {
    DELETE_REQUESTED = 'DELETE_REQUESTED',
    DELETED = 'DELETED',
    ACTIVE = 'ACTIVE',
}

enum ContractualType {
    RMS = 'RMS',
    ADMIN = 'ADMIN',
    CITY_POP = 'CITY_POP',
    WEWASH_BOX = 'WWB',
    WEWASH_CARE = 'WWC',
    WEWASH_CARE_IDOS = 'WWC_IDOS',
    WHITE_LABEL_WWB = 'WHITE_LABEL_WWB',
    WHITE_LABEL_WWC = 'WHITE_LABEL_WWC',
    WEWASH_CARE_MINIMUM_USAGE = 'WWC_MINIMUM_USAGE',
    WEWASH_CARE_IDOS_MINIMUM_USAGE = 'WWC_IDOS_MINIMUM_USAGE',
}

export enum PropertyManagementSystemProvider {
    APALEO = 'APALEO',
    RMS_CLOUD = 'RMS_CLOUD',
}

export interface LocationCostIncreaseSuggestionDto {
    cost_washer: number;
    cost_dryer: number;
    reason_en: string;
    reason_de: string;
}

export interface LocationDto {
    id: number;
    external_id: string;
    name: string;
    address: string;
    note: string;
    country_id: number;
    gps_lat: number;
    gps_lon: number;
    no_of_appliances_w?: number;
    no_of_appliances_d?: number;
    mansion_id?: number;
    serial_number: string;
    currency: CurrencyType;
    needs_gateway?: boolean;
    booking_logic?: BookingLogicPolicy;
    booking_minutes?: number;
    reservation_minutes?: number;
    pickup_minutes?: number;
    timezone_id: number;
    hotel_code?: string;
    pms_property_id?: number;
    property_management_system_provider?: PropertyManagementSystemProvider;
    contractual_type?: ContractualType;
    cost_increase_suggestion?: LocationCostIncreaseSuggestionDto;
    lifecycle_status?: LocationLifecycleStatus;
    partner_manages_phone_users?: boolean;
    phone_user_dial_number?: string;
}

interface LocationWithCostsDto {
    id?: number;
    name: string;
    gps_lat: number;
    gps_lon: number;
    mansion_id: number;
    external_id: string;
    num_of_appartments?: number;
    dryer_cost?: number;
    washer_cost?: number;
    note: string;
    needs_gateway: boolean;
    booking_logic: BookingLogicPolicy;
}

export interface LocationOverviewDto extends LocationWithCostsDto {
    no_of_appliances?: number;
    currency_type?: CurrencyType;
    gateway_serial_number?: string;
    connectivity_state?: ConnectivityState;
    connectivity_check_state?: ConnectivityCheckState;
    connectivity_check_update_time?: number;
    cost_increase_suggestion?: LocationCostIncreaseSuggestionDto;
    lifecycle_status?: LocationLifecycleStatus;
}

interface LocationWriteDto extends LocationWithCostsDto {
    currency_type: CurrencyType;
    reservation_minutes: number;
    booking_minutes?: number;
    pickup_minutes: number;
    gateway_serial_number?: string;
}

export interface LocationsByMansion {
    [locationId: number]: LocationOverviewDto;
}

export interface MansionById {
    [mansionId: number]: LocationsByMansion;
}

export interface PhoneUserAtLocationDto {
    id: string;
    phone: string;
    email: string;
}

export interface CreatePhoneUserAtLocationDto {
    phone: string;
    email: string;
    terms_and_conditions_accepted: boolean;
}

interface PhoneUsersForLocationDto {
    list: PhoneUserAtLocationDto[];
}

interface LocationsStore {
    location?: LocationDto;
    locationsByMansion?: LocationOverviewDto[];
    allLocationsForAccountingArea?: LocationOverviewDto[];
    locationsByMansionsById: MansionById;
    phoneUsersForLocation?: PhoneUsersForLocationDto;
}

const initialState: LocationsStore = {
    location: undefined,
    locationsByMansion: undefined,
    allLocationsForAccountingArea: undefined,
    locationsByMansionsById: {},
    phoneUsersForLocation: undefined,
};

const locationsSlice = createSlice({
    name: 'locations',
    initialState,
    reducers: {
        setLocation(state: LocationsStore, action: PayloadAction<LocationDto>) {
            state.location = action.payload;
        },
        setLocationsByMansion(
            state: LocationsStore,
            action: PayloadAction<LocationOverviewDto[]>
        ) {
            state.locationsByMansion = action.payload;
        },
        setAllLocationsForAccountingArea(
            state: LocationsStore,
            action: PayloadAction<LocationOverviewDto[]>
        ) {
            state.allLocationsForAccountingArea = action.payload;
        },
        setPhoneUsersForLocation(
            state: LocationsStore,
            action: PayloadAction<PhoneUsersForLocationDto>
        ) {
            state.phoneUsersForLocation = action.payload;
        },
        rememberLocations(state, action) {
            action.payload.forEach((location) => {
                if (!state.locationsByMansionsById[location.mansion_id]) {
                    state.locationsByMansionsById[location.mansion_id] = {};
                }
                const oldData =
                    state.locationsByMansionsById[location.mansion_id][
                        location.id
                    ];
                state.locationsByMansionsById[location.mansion_id][
                    location.id
                ] = { ...oldData, ...location };
            });
        },
        resetLocations(state) {
            state.locationsByMansionsById =
                initialState.locationsByMansionsById;
        },
    },
});

export const { rememberLocations, resetLocations } = locationsSlice.actions;

export function resetRemoveLocation() {
    return (dispatch) => {
        dispatch(pbRequestReset(PbRequest.REMOVE_LOCATION));
    };
}

export function RemoveLocation(locationId: number, onSuccess?: () => void) {
    return async (dispatch: PbDispatch) => {
        try {
            const request: RequestInit = {
                method: 'DELETE',
            };
            await processRequestForAccountingAreaWithReplacement<LocationDto>(
                dispatch,
                `${API}/location/${locationId}/${ACCOUNTING_AREA_ID_MARKER}`,
                request,
                PbRequest.REMOVE_LOCATION
            );

            dispatch(resetLocations());

            onSuccess?.();
        } catch {}
    };
}

export function LoadLocation(locationId: number) {
    return async (dispatch: PbDispatch) => {
        try {
            const location =
                await processRequestForAccountingAreaWithReplacement<LocationDto>(
                    dispatch,
                    `${API}/locations/${locationId}/${ACCOUNTING_AREA_ID_MARKER}`,
                    { method: 'GET' },
                    PbRequest.GET_LOCATION
                );
            dispatch(locationsSlice.actions.setLocation(location));
            if (location.mansion_id) {
                const locationWithCosts: LocationOverviewDto = {
                    ...location,
                    gateway_serial_number: location.serial_number,
                    mansion_id: location.mansion_id || 0,
                    needs_gateway: !!location.needs_gateway,
                    booking_logic:
                        location.booking_logic ||
                        BookingLogicPolicy.APPLIANCE_START,
                    lifecycle_status: location.lifecycle_status,
                };
                dispatch(rememberLocations([locationWithCosts]));
            }
        } catch (e) {
            console.log('failed to get laundry room', e);
        }
    };
}

export function LoadLocationsForMansionId(mansionId: number) {
    return async (dispatch: PbDispatch) => {
        try {
            const locationList =
                await processRequestForAccountingAreaWithReplacement<
                    LocationOverviewDto[]
                >(
                    dispatch,
                    `${API}/mansions/${mansionId}/locations/${ACCOUNTING_AREA_ID_MARKER}`,
                    { method: 'GET' },
                    PbRequest.GET_LOCATIONS_BY_MANSION
                );
            dispatch(
                locationsSlice.actions.setLocationsByMansion(locationList)
            );
            dispatch(rememberLocations(locationList));
        } catch (e) {
            console.log('failed to get laundry rooms for mansion', e);
        }
    };
}

export function LoadAllLocationsForAccountingArea() {
    return async (dispatch: PbDispatch) => {
        try {
            const locationList =
                await processRequestForAccountingAreaWithReplacement<
                    LocationOverviewDto[]
                >(
                    dispatch,
                    `${API}/mansions/all/locations/${ACCOUNTING_AREA_ID_MARKER}`,
                    { method: 'GET' },
                    PbRequest.GET_ALL_LOCATIONS_FOR_ACCOUNTING_AREA
                );
            dispatch(
                locationsSlice.actions.setAllLocationsForAccountingArea(
                    locationList
                )
            );
            dispatch(rememberLocations(locationList));
        } catch (e) {
            console.log('failed to get laundry rooms', e);
        }
    };
}

export function CreateLocation(
    location: LocationWriteDto,
    onSuccess?: () => void
) {
    return async (dispatch: PbDispatch) => {
        const request: RequestInit = {
            method: 'POST',
            body: JSON.stringify(location),
        };

        try {
            const locationId =
                await processRequestForAccountingAreaWithReplacement<number>(
                    dispatch,
                    `${API}/locations/${ACCOUNTING_AREA_ID_MARKER}`,
                    request,
                    PbRequest.CREATE_LOCATION
                );
            const locationWithCosts: LocationOverviewDto = {
                ...location,
                id: locationId,
            };
            dispatch(rememberLocations([locationWithCosts]));
            onSuccess?.();
        } catch (e) {
            console.log('failed to create laundry room', e);
        }
    };
}

export function UpdateLocation(location: LocationWriteDto) {
    return async (dispatch: PbDispatch) => {
        const request: RequestInit = {
            method: 'POST',
            body: JSON.stringify(location),
        };
        try {
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/locations/${location.id}/${ACCOUNTING_AREA_ID_MARKER}`,
                request,
                PbRequest.UPDATE_LOCATION
            );
            const locationWithCosts: LocationOverviewDto = location;
            dispatch(rememberLocations([locationWithCosts]));
        } catch (e) {
            console.log('failed to update laundry room', e);
        }
    };
}

export function resetConnectivityCheckRequestState() {
    return (dispatch: PbDispatch) => {
        dispatch(pbRequestReset(PbRequest.REQUEST_CONNECTIVITY_CHECK));
    };
}

export function RequestConnectivityCheck(
    locationId: number,
    onSuccess?: () => void
) {
    return async (dispatch: PbDispatch) => {
        try {
            const request = { method: 'POST' };
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/accounting_area/${ACCOUNTING_AREA_ID_MARKER}/locations/${locationId}/connectivity_check`,
                request,
                PbRequest.REQUEST_CONNECTIVITY_CHECK
            );
            onSuccess?.();
        } catch {}
    };
}

export function GetPhoneUsersForLocation(locationId: number) {
    return async (dispatch: PbDispatch) => {
        try {
            const phoneUsersResponse =
                await processRequestForAccountingAreaWithReplacement<PhoneUsersForLocationDto>(
                    dispatch,
                    `${API}/${ACCOUNTING_AREA_ID_MARKER}/locations/${locationId}/phone-users`,
                    { method: 'GET' },
                    PbRequest.GET_PHONE_USERS_FOR_LOCATION
                );
            dispatch(
                locationsSlice.actions.setPhoneUsersForLocation(
                    phoneUsersResponse
                )
            );
        } catch {}
    };
}

export function DeletePhoneUserForLocation(locationId: number, userId: string) {
    return async (dispatch: PbDispatch) => {
        try {
            await processRequestForAccountingAreaWithReplacement<PhoneUsersForLocationDto>(
                dispatch,
                `${API}/${ACCOUNTING_AREA_ID_MARKER}/locations/${locationId}/phone-users/${userId}`,
                { method: 'DELETE' },
                PbRequest.DELETE_PHONE_USER_FOR_LOCATION
            );
            await GetPhoneUsersForLocation(locationId)(dispatch);
        } catch {}
    };
}

export function AddPhoneUserForLocation(locationId: number, createPhoneUser: CreatePhoneUserAtLocationDto) {
    return async (dispatch: PbDispatch) => {
        try {
            await processRequestForAccountingAreaWithReplacement<PhoneUsersForLocationDto>(
                dispatch,
                `${API}/${ACCOUNTING_AREA_ID_MARKER}/locations/${locationId}/phone-users`,
                { method: 'POST', body: JSON.stringify(createPhoneUser) },
                PbRequest.ADD_PHONE_USER_FOR_LOCATION
            );
            await GetPhoneUsersForLocation(locationId)(dispatch);
        } catch {}
    };
}

export default locationsSlice.reducer;
